import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { 
  Calendar, MapPin, Clock, Users, ArrowLeft, Loader2, Upload, X, 
  FileText, Video, ExternalLink, Edit2, Save, Eye, UserCheck, 
  CheckCircle, XCircle, Mail
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { trialAPI } from '../services/api';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';

const TrialDetail = () => {
  const { trialId } = useParams();
  const navigate = useNavigate();
  const [trial, setTrial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [applying, setApplying] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applicants, setApplicants] = useState([]);
  const [loadingApplicants, setLoadingApplicants] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  
  // Application form state
  const [message, setMessage] = useState('');
  const [cvFile, setCvFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);

  // Edit form state
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    requirements: '',
    date: '',
    time: '',
    location: '',
    sport: '',
    status: ''
  });

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isOwner = trial?.clubId === currentUser._id || trial?.club?._id === currentUser._id;

  useEffect(() => {
    fetchTrial();
  }, [trialId]);

  useEffect(() => {
    if (isOwner && trial) {
      fetchApplicants();
    }
  }, [isOwner, trial]);

  const fetchTrial = async () => {
    try {
      setLoading(true);
      const response = await trialAPI.getTrial(trialId);
      setTrial(response.data);
      // Initialize edit form
      setEditForm({
        title: response.data.title || '',
        description: response.data.description || '',
        requirements: response.data.requirements || '',
        date: response.data.date ? response.data.date.split('T')[0] : '',
        time: response.data.time || '',
        location: response.data.location || '',
        sport: response.data.sport || '',
        status: response.data.status || 'open'
      });
    } catch (error) {
      console.error('Error fetching trial:', error);
      toast.error('Failed to load trial details');
      navigate('/trials');
    } finally {
      setLoading(false);
    }
  };

  const fetchApplicants = async () => {
    try {
      setLoadingApplicants(true);
      const response = await trialAPI.getTrialApplications(trialId);
      setApplicants(response.data.applications || []);
    } catch (error) {
      console.error('Error fetching applicants:', error);
    } finally {
      setLoadingApplicants(false);
    }
  };

  const handleSaveChanges = async () => {
    try {
      setSaving(true);
      await trialAPI.updateTrial(trialId, editForm);
      toast.success('Trial updated successfully!');
      setIsEditMode(false);
      fetchTrial();
    } catch (error) {
      console.error('Error updating trial:', error);
      toast.error('Failed to update trial');
    } finally {
      setSaving(false);
    }
  };

  const handleCvUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('CV file must be less than 5MB');
        return;
      }
      setCvFile(file);
    }
  };

  const handleVideoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 50 * 1024 * 1024) {
        toast.error('Video file must be less than 50MB');
        return;
      }
      setVideoFile(file);
    }
  };

  const handleSubmitApplication = async () => {
    if (!message.trim()) {
      toast.error('Please write a message');
      return;
    }

    setApplying(true);

    try {
      await trialAPI.applyToTrial(trialId, message, cvFile, videoFile);
      toast.success('Application submitted successfully!');
      setShowApplicationForm(false);
      setMessage('');
      setCvFile(null);
      setVideoFile(null);
    } catch (error) {
      console.error('Error submitting application:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to submit application';
      toast.error(errorMessage);
    } finally {
      setApplying(false);
    }
  };

  const getDefaultImage = (sport) => {
    const images = {
      'Football': 'https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=800',
      'Basketball': 'https://images.unsplash.com/photo-1546519638-68e109498ffc?w=800',
      'Track & Field': 'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=800',
    };
    return images[sport] || 'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=800';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'applied': return 'bg-primary/10 text-primary';
      case 'shortlisted': return 'bg-purple-400/10 text-purple-500';
      case 'accepted': return 'bg-purple-600/10 text-purple-600';
      case 'rejected': return 'bg-destructive/10 text-destructive';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pb-20 md:pb-0">
        <Navbar />
        <div className="flex items-center justify-center py-12 pt-32">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!trial) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 pt-20">
        <div className="py-6">
          {/* Back Button & Actions */}
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            
            {isOwner && (
              <div className="flex gap-2">
                {isEditMode ? (
                  <>
                    <Button variant="outline" onClick={() => setIsEditMode(false)}>
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleSaveChanges}
                      disabled={saving}
                      className="btn-primary"
                      className="text-white"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                      Save Changes
                    </Button>
                  </>
                ) : (
                  <Button 
                    variant="outline" 
                    onClick={() => setIsEditMode(true)}
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit Trial
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Trial Header Image */}
          <Card className="overflow-hidden rounded-2xl mb-6">
            <div className="relative h-64 md:h-80">
              <img
                src={trial.imageUrl || getDefaultImage(trial.sport)}
                alt={trial.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 right-4">
                <Badge className="badge-primary text-sm">
                  {trial.sport}
                </Badge>
              </div>
              {isOwner && (
                <div className="absolute top-4 left-4">
                  <Badge className={trial.status === 'open' ? 'bg-primary' : 'bg-muted'}>
                    {trial.status === 'open' ? 'Open' : 'Closed'}
                  </Badge>
                </div>
              )}
            </div>
          </Card>

          {/* Owner Tabs */}
          {isOwner ? (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="w-full justify-start bg-card rounded-lg p-1 shadow-sm mb-6 border border-border">
                <TabsTrigger value="details" className="flex-1">
                  <Eye className="w-4 h-4 mr-2" />
                  Details
                </TabsTrigger>
                <TabsTrigger value="applicants" className="flex-1">
                  <Users className="w-4 h-4 mr-2" />
                  Applicants ({applicants.length})
                </TabsTrigger>
              </TabsList>

              {/* Details Tab */}
              <TabsContent value="details">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Main Content */}
                  <div className="lg:col-span-2">
                    <Card className="rounded-2xl">
                      <CardContent className="p-6">
                        {isEditMode ? (
                          // Edit Mode
                          <div className="space-y-4">
                            <div>
                              <Label htmlFor="title">Title</Label>
                              <Input
                                id="title"
                                value={editForm.title}
                                onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label htmlFor="description">Description</Label>
                              <Textarea
                                id="description"
                                value={editForm.description}
                                onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                                rows={5}
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label htmlFor="requirements">Requirements</Label>
                              <Textarea
                                id="requirements"
                                value={editForm.requirements}
                                onChange={(e) => setEditForm({...editForm, requirements: e.target.value})}
                                rows={3}
                                className="mt-1"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor="date">Date</Label>
                                <Input
                                  id="date"
                                  type="date"
                                  value={editForm.date}
                                  onChange={(e) => setEditForm({...editForm, date: e.target.value})}
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label htmlFor="time">Time</Label>
                                <Input
                                  id="time"
                                  value={editForm.time}
                                  onChange={(e) => setEditForm({...editForm, time: e.target.value})}
                                  placeholder="e.g., 10:00 AM"
                                  className="mt-1"
                                />
                              </div>
                            </div>
                            <div>
                              <Label htmlFor="location">Location</Label>
                              <Input
                                id="location"
                                value={editForm.location}
                                onChange={(e) => setEditForm({...editForm, location: e.target.value})}
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label htmlFor="status">Status</Label>
                              <select
                                id="status"
                                value={editForm.status}
                                onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                                className="w-full p-2 border rounded-md mt-1 bg-background text-foreground border-border"
                              >
                                <option value="open">Open</option>
                                <option value="closed">Closed</option>
                              </select>
                            </div>
                          </div>
                        ) : (
                          // View Mode
                          <>
                            <h1 className="text-3xl font-bold text-foreground mb-4">{trial.title}</h1>

                            {/* Club Info */}
                            {trial.club && (
                              <div 
                                className="flex items-center space-x-3 mb-6 p-4 bg-secondary rounded-xl cursor-pointer hover:opacity-80"
                                onClick={() => navigate(`/profile/${trial.club.username}`)}
                              >
                                <Avatar className="w-12 h-12">
                                  <AvatarImage src={trial.club.profileImage} alt={trial.club.name} />
                                  <AvatarFallback>{trial.club.name?.[0]}</AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="font-semibold text-foreground">{trial.club.name}</p>
                                  <p className="text-sm text-muted-foreground">@{trial.club.username}</p>
                                </div>
                              </div>
                            )}

                            {/* Description */}
                            <div className="mb-6">
                              <h3 className="text-lg font-bold text-foreground mb-2">Description</h3>
                              <p className="text-muted-foreground whitespace-pre-wrap">{trial.description}</p>
                            </div>

                            {/* Requirements */}
                            {trial.requirements && (
                              <div className="mb-6">
                                <h3 className="text-lg font-bold text-foreground mb-2">Requirements</h3>
                                <p className="text-muted-foreground whitespace-pre-wrap">{trial.requirements}</p>
                              </div>
                            )}
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Sidebar */}
                  <div className="lg:col-span-1">
                    <Card className="rounded-2xl sticky top-24">
                      <CardContent className="p-6">
                        <h3 className="text-lg font-bold text-foreground mb-4">Trial Details</h3>
                        
                        <div className="space-y-3 mb-6">
                          <div className="flex items-start">
                            <Calendar className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 text-primary" />
                            <div>
                              <p className="text-sm font-semibold text-foreground">Date</p>
                              <p className="text-sm text-muted-foreground">
                                {new Date(trial.date).toLocaleDateString('en-US', { 
                                  weekday: 'long', 
                                  year: 'numeric', 
                                  month: 'long', 
                                  day: 'numeric' 
                                })}
                              </p>
                            </div>
                          </div>

                          {trial.time && (
                            <div className="flex items-start">
                              <Clock className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 text-primary" />
                              <div>
                                <p className="text-sm font-semibold text-foreground">Time</p>
                                <p className="text-sm text-muted-foreground">{trial.time}</p>
                              </div>
                            </div>
                          )}

                          <div className="flex items-start">
                            <MapPin className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 text-primary" />
                            <div>
                              <p className="text-sm font-semibold text-foreground">Location</p>
                              <p className="text-sm text-muted-foreground">{trial.location}</p>
                            </div>
                          </div>

                          <div className="flex items-start">
                            <Users className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 text-primary" />
                            <div>
                              <p className="text-sm font-semibold text-foreground">Applicants</p>
                              <p className="text-sm text-muted-foreground">{trial.applicationsCount || 0} applications</p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </TabsContent>

              {/* Applicants Tab */}
              <TabsContent value="applicants">
                <Card className="rounded-2xl">
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Users className="w-5 h-5 mr-2 text-primary" />
                      Trial Applicants ({applicants.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loadingApplicants ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-primary" />
                      </div>
                    ) : applicants.length === 0 ? (
                      <div className="text-center py-8">
                        <Users className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                        <p className="text-muted-foreground">No applicants yet</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {applicants.map((app) => (
                          <div 
                            key={app._id}
                            className="flex items-center justify-between p-4 border rounded-xl hover:shadow-md transition-shadow bg-card"
                          >
                            <div 
                              className="flex items-center gap-4 flex-1 cursor-pointer"
                              onClick={() => navigate(`/profile/${app.athlete?.username}`)}
                            >
                              <Avatar className="w-14 h-14">
                                <AvatarImage src={app.athlete?.profileImage} alt={app.athlete?.name} />
                                <AvatarFallback>{app.athlete?.name?.[0] || 'A'}</AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-foreground">{app.athlete?.name}</h4>
                                <p className="text-sm text-muted-foreground">@{app.athlete?.username}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  {app.athlete?.location && (
                                    <span className="text-xs text-muted-foreground flex items-center">
                                      <MapPin className="w-3 h-3 mr-1" />
                                      {app.athlete.location}
                                    </span>
                                  )}
                                  <Badge className={`text-xs ${getStatusColor(app.status)}`}>
                                    {app.status}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(`/profile/${app.athlete?.username}`)}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                Profile
                              </Button>
                              <Button
                                size="sm"
                                className="btn-primary"
                                className="text-white"
                                onClick={() => navigate(`/messages?userId=${app.athlete?._id}`)}
                              >
                                <Mail className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          ) : (
            // Non-owner view (original layout)
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Content */}
              <div className="lg:col-span-2">
                <Card className="rounded-2xl">
                  <CardContent className="p-6">
                    <h1 className="text-3xl font-bold text-foreground mb-4">{trial.title}</h1>

                    {/* Club Info */}
                    {trial.club && (
                      <div 
                        className="flex items-center space-x-3 mb-6 p-4 bg-secondary rounded-xl cursor-pointer hover:opacity-80"
                        onClick={() => navigate(`/profile/${trial.club.username}`)}
                      >
                        <Avatar className="w-12 h-12">
                          <AvatarImage src={trial.club.profileImage} alt={trial.club.name} />
                          <AvatarFallback>{trial.club.name?.[0]}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-semibold text-foreground">{trial.club.name}</p>
                          <p className="text-sm text-muted-foreground">@{trial.club.username}</p>
                        </div>
                      </div>
                    )}

                    {/* Description */}
                    <div className="mb-6">
                      <h3 className="text-lg font-bold text-foreground mb-2">Description</h3>
                      <p className="text-muted-foreground whitespace-pre-wrap">{trial.description}</p>
                    </div>

                    {/* Requirements */}
                    {trial.requirements && (
                      <div className="mb-6">
                        <h3 className="text-lg font-bold text-foreground mb-2">Requirements</h3>
                        <p className="text-muted-foreground whitespace-pre-wrap">{trial.requirements}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1">
                <Card className="rounded-2xl sticky top-24">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-foreground mb-4">Trial Details</h3>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex items-start">
                        <Calendar className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 text-primary" />
                        <div>
                          <p className="text-sm font-semibold text-foreground">Date</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(trial.date).toLocaleDateString('en-US', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                          </p>
                        </div>
                      </div>

                      {trial.time && (
                        <div className="flex items-start">
                          <Clock className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 text-primary" />
                          <div>
                            <p className="text-sm font-semibold text-foreground">Time</p>
                            <p className="text-sm text-muted-foreground">{trial.time}</p>
                          </div>
                        </div>
                      )}

                      <div className="flex items-start">
                        <MapPin className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 text-primary" />
                        <div>
                          <p className="text-sm font-semibold text-foreground">Location</p>
                          <p className="text-sm text-muted-foreground">{trial.location}</p>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <Users className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 text-primary" />
                        <div>
                          <p className="text-sm font-semibold text-foreground">Applicants</p>
                          <p className="text-sm text-muted-foreground">{trial.applicationsCount || 0} applications</p>
                        </div>
                      </div>
                    </div>

                    {/* Apply Button */}
                    {trial.status === 'open' && (
                      <div className="space-y-3">
                        {!showApplicationForm ? (
                          <Button 
                            className="w-full text-white"
                            className="btn-primary"
                            onClick={() => setShowApplicationForm(true)}
                          >
                            Apply Now
                          </Button>
                        ) : (
                          <div className="space-y-3">
                            <Label>Your Message</Label>
                            <Textarea
                              placeholder="Tell the club why you're interested..."
                              value={message}
                              onChange={(e) => setMessage(e.target.value)}
                              rows={4}
                            />
                            
                            {/* CV Upload */}
                            <div>
                              <Label className="flex items-center">
                                <FileText className="w-4 h-4 mr-2" />
                                CV/Resume (optional)
                              </Label>
                              <Input
                                type="file"
                                accept=".pdf,.doc,.docx"
                                onChange={handleCvUpload}
                                className="mt-1"
                              />
                              {cvFile && (
                                <p className="text-xs text-primary mt-1 flex items-center">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  {cvFile.name}
                                </p>
                              )}
                            </div>

                            {/* Video Upload */}
                            <div>
                              <Label className="flex items-center">
                                <Video className="w-4 h-4 mr-2" />
                                Highlight Video (optional)
                              </Label>
                              <Input
                                type="file"
                                accept="video/*"
                                onChange={handleVideoUpload}
                                className="mt-1"
                              />
                              {videoFile && (
                                <p className="text-xs text-primary mt-1 flex items-center">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  {videoFile.name}
                                </p>
                              )}
                            </div>

                            <div className="flex gap-2">
                              <Button 
                                variant="outline"
                                className="flex-1"
                                onClick={() => {
                                  setShowApplicationForm(false);
                                  setMessage('');
                                  setCvFile(null);
                                  setVideoFile(null);
                                }}
                              >
                                Cancel
                              </Button>
                              <Button 
                                className="flex-1 text-white"
                                className="btn-primary"
                                onClick={handleSubmitApplication}
                                disabled={applying}
                              >
                                {applying ? (
                                  <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Submitting...
                                  </>
                                ) : (
                                  'Submit Application'
                                )}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {trial.status === 'closed' && (
                      <div className="p-4 bg-muted rounded-lg text-center">
                        <p className="text-muted-foreground text-sm">This trial is no longer accepting applications</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrialDetail;
